/**
 * Cache invalidation helpers for WingWise mutations.
 *
 * IMPORTANT: only invalidate what actually depends on the mutated data. Broad
 * "invalidate everything" behaviour hides bugs and thrashes the network.
 */
import { queryClient } from "./queryClient";
import { qk, keys } from "./queryKeys";

const _invalPrefix = (prefix) => queryClient.invalidateQueries({ queryKey: prefix });

export const invalidate = {
  // After approving a proposal — refresh trade lifecycle, portfolio, positions, risk views.
  onProposalApproved: (proposalId, symbol) => {
    _invalPrefix(keys.proposal(proposalId));
    _invalPrefix(keys.proposalVersions(proposalId));
    _invalPrefix(qk(["proposal"], { userScoped: true }));
    _invalPrefix(keys.positions("open"));
    _invalPrefix(keys.portfolios());
    _invalPrefix(qk(["risk"], { userScoped: false }));
    _invalPrefix(keys.opportunities());
    if (symbol) _invalPrefix(keys.opportunityDetail(symbol));
  },

  // Position closed — refresh portfolio, positions, ledger, replay list, coaching.
  onPositionClosed: (symbol) => {
    _invalPrefix(keys.positions("open"));
    _invalPrefix(keys.positions("closed"));
    _invalPrefix(keys.portfolios());
    _invalPrefix(keys.replayList());
    _invalPrefix(keys.coaching());
    if (symbol) _invalPrefix(keys.opportunityDetail(symbol));
  },

  // Note CRUD — refresh only the specific entity's note list.
  onNoteChanged: (entityType, entityId) => {
    _invalPrefix(keys.notes(entityType, entityId));
  },

  // Risk fix applied — proposal + sizing + risk views.
  onRiskFixApplied: (proposalId) => {
    _invalPrefix(keys.proposal(proposalId));
    _invalPrefix(qk(["risk"], { userScoped: false }));
  },

  // Refreshing analysis for a symbol — analysis, opportunity, why, memory.
  onAnalysisRefreshed: (symbol) => {
    if (symbol) {
      _invalPrefix(keys.opportunityDetail(symbol));
      _invalPrefix(keys.analysisExisting(symbol));
      _invalPrefix(keys.marketMemory(symbol));
    }
    _invalPrefix(keys.analysisHistory());
  },

  // Regime refreshed — regime + daily brief + sizing (which depends on regime multiplier).
  onRegimeRefreshed: () => {
    _invalPrefix(keys.regime());
    _invalPrefix(keys.dailyBrief());
    _invalPrefix(qk(["risk", "sizing"], { userScoped: true }));
    _invalPrefix(keys.opportunities());
  },

  // Saved-view CRUD.
  onSavedViewChanged: () => {
    _invalPrefix(keys.savedViews());
  },

  // Coaching feedback.
  onCoachingFeedback: () => {
    _invalPrefix(keys.coaching());
  },
};

// Aggressive helper for logout/workspace switch — clears everything.
export function clearAllCache() {
  queryClient.clear();
}
